import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const CYAN = '#00F3FF';
const PURPLE = '#BC13FE';
const BG = '#030305';
const CARD_BG = '#0a0a12';
const BORDER = '#1a1a2e';

const styles = {
  page: {
    background: BG,
    minHeight: '100vh',
    color: '#e0e0e0',
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    paddingTop: 100,
    paddingBottom: 80,
  },
  hero: {
    textAlign: 'center',
    padding: '60px 20px 40px',
    background: 'linear-gradient(180deg, #00050d 0%, #030305 100%)',
    position: 'relative',
    overflow: 'hidden',
  },
  heroBadge: {
    display: 'inline-block',
    background: `linear-gradient(135deg, ${CYAN}22, ${PURPLE}22)`,
    border: `1px solid ${CYAN}55`,
    borderRadius: 30,
    padding: '6px 20px',
    fontSize: 12,
    letterSpacing: 3,
    color: CYAN,
    fontWeight: 700,
    marginBottom: 20,
    textTransform: 'uppercase',
  },
  heroTitle: {
    fontSize: 'clamp(2rem, 5vw, 3.5rem)',
    fontWeight: 900,
    margin: '0 0 16px',
    background: `linear-gradient(135deg, #fff 0%, ${CYAN} 50%, ${PURPLE} 100%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    lineHeight: 1.15,
  },
  heroSub: {
    color: '#888',
    fontSize: '1.1rem',
    maxWidth: 620,
    margin: '0 auto 30px',
    lineHeight: 1.7,
  },
  priceBadge: {
    display: 'inline-block',
    background: `linear-gradient(135deg, ${CYAN}33, ${PURPLE}33)`,
    border: `1px solid ${PURPLE}88`,
    borderRadius: 30,
    padding: '8px 24px',
    fontSize: 16,
    color: '#fff',
    fontWeight: 700,
  },
  glowOrb: {
    position: 'absolute',
    borderRadius: '50%',
    filter: 'blur(80px)',
    opacity: 0.15,
    pointerEvents: 'none',
  },
  section: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '40px 20px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 28,
    flexWrap: 'wrap',
  },
  sectionTitle: {
    fontSize: '1.4rem',
    fontWeight: 800,
    color: '#fff',
    margin: 0,
  },
  sectionTag: {
    background: `linear-gradient(135deg, ${CYAN}22, ${PURPLE}22)`,
    border: `1px solid ${CYAN}44`,
    borderRadius: 20,
    padding: '3px 12px',
    fontSize: 11,
    color: CYAN,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  divider: {
    borderColor: BORDER,
    margin: '10px 0 32px',
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 22,
  },
  card: {
    background: CARD_BG,
    border: `1px solid ${BORDER}`,
    borderRadius: 16,
    padding: '24px 22px 20px',
    transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
    cursor: 'default',
    position: 'relative',
    overflow: 'hidden',
  },
  cardHover: {
    transform: 'translateY(-6px)',
    boxShadow: `0 20px 50px ${CYAN}1a`,
    borderColor: `${CYAN}55`,
  },
  vramBadge: {
    display: 'inline-block',
    background: `linear-gradient(135deg, ${CYAN}33, ${PURPLE}33)`,
    border: `1px solid ${CYAN}66`,
    borderRadius: 20,
    padding: '3px 12px',
    fontSize: 11,
    fontWeight: 700,
    color: CYAN,
    letterSpacing: 1,
    marginBottom: 10,
    textTransform: 'uppercase',
  },
  cardTitle: {
    fontSize: '1.05rem',
    fontWeight: 700,
    color: '#fff',
    marginBottom: 14,
  },
  specTable: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: 16,
  },
  specRow: {
    borderBottom: `1px solid ${BORDER}`,
  },
  specKey: {
    padding: '7px 2px',
    color: '#555',
    fontSize: 12,
    width: '42%',
  },
  specVal: {
    padding: '7px 2px',
    color: '#bbb',
    fontSize: 12,
    fontWeight: 600,
    textAlign: 'right',
  },
  price: {
    fontSize: '1.6rem',
    fontWeight: 900,
    color: CYAN,
    margin: '14px 0 3px',
  },
  priceNote: {
    fontSize: 11,
    color: '#444',
    marginBottom: 18,
  },
  btnRow: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  waBtn: {
    flex: 1,
    minWidth: 110,
    padding: '9px 12px',
    background: 'linear-gradient(135deg, #25D366, #128C7E)',
    border: 'none',
    borderRadius: 9,
    color: '#fff',
    fontWeight: 700,
    fontSize: 13,
    cursor: 'pointer',
    textAlign: 'center',
    textDecoration: 'none',
    display: 'inline-block',
  },
  quoteBtn: {
    flex: 1,
    minWidth: 110,
    padding: '9px 12px',
    background: `linear-gradient(135deg, ${CYAN}18, ${PURPLE}18)`,
    border: `1px solid ${CYAN}55`,
    borderRadius: 9,
    color: CYAN,
    fontWeight: 700,
    fontSize: 13,
    textAlign: 'center',
    textDecoration: 'none',
    display: 'inline-block',
  },
  gpuSpecBox: {
    background: CARD_BG,
    border: `1px solid ${BORDER}`,
    borderRadius: 16,
    padding: '28px 28px 24px',
    maxWidth: 1200,
    margin: '0 auto 60px',
  },
  gpuSpecGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
    gap: 16,
    marginTop: 22,
  },
  gpuSpecItem: {
    background: '#0f0f1a',
    border: `1px solid ${BORDER}`,
    borderRadius: 11,
    padding: '14px 16px',
    textAlign: 'center',
  },
  gpuSpecLabel: {
    fontSize: 11,
    color: '#555',
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  gpuSpecValue: {
    fontSize: '1.1rem',
    fontWeight: 800,
    color: CYAN,
  },
};

const openWA = (planName) => {
  const text = encodeURIComponent(`Hi, I'm interested in the ${planName} plan on IndiaCloud. Please share more details.`);
  window.open(`https://wa.me/919873385555?text=${text}`, '_blank');
};

function PlanCard({ plan }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{ ...styles.card, ...(hovered ? styles.cardHover : {}) }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ position: 'absolute', top: 0, right: 0, width: 100, height: 100,
        background: `radial-gradient(circle at top right, ${PURPLE}1a, transparent 70%)`, pointerEvents: 'none' }} />
      <div style={styles.vramBadge}>{plan.vramLabel}</div>
      <div style={styles.cardTitle}>{plan.name}</div>
      <table style={styles.specTable}>
        <tbody>
          {plan.specs.map(([k, v]) => (
            <tr key={k} style={styles.specRow}>
              <td style={styles.specKey}>{k}</td>
              <td style={styles.specVal}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={styles.price}>{plan.price}</div>
      <div style={styles.priceNote}>{plan.note}</div>
      <div style={styles.btnRow}>
        <button style={styles.waBtn} onClick={() => openWA(plan.name)}>💬 WhatsApp</button>
        <Link to="/contact" style={styles.quoteBtn}>Get Quote →</Link>
      </div>
    </div>
  );
}

const sharedPlans = [
  {
    name: 'A100 Shared — 8GB VRAM',
    vramLabel: '8GB VRAM',
    price: '₹3,999/mo',
    note: '+ GST • Shared GPU',
    specs: [['vCPU', '8 Cores'], ['RAM', '16 GB'], ['NVMe SSD', '240 GB'], ['VRAM', '8 GB A100']],
  },
  {
    name: 'A100 Shared — 16GB VRAM',
    vramLabel: '16GB VRAM',
    price: '₹7,999/mo',
    note: '+ GST • Shared GPU',
    specs: [['vCPU', '16 Cores'], ['RAM', '32 GB'], ['NVMe SSD', '500 GB'], ['VRAM', '16 GB A100']],
  },
  {
    name: 'A100 Shared — 24GB VRAM',
    vramLabel: '24GB VRAM',
    price: '₹11,999/mo',
    note: '+ GST • Shared GPU',
    specs: [['vCPU', '24 Cores'], ['RAM', '64 GB'], ['NVMe SSD', '1 TB'], ['VRAM', '24 GB A100']],
  },
  {
    name: 'A100 Shared — 32GB VRAM',
    vramLabel: '32GB VRAM',
    price: '₹15,999/mo',
    note: '+ GST • Shared GPU',
    specs: [['vCPU', '32 Cores'], ['RAM', '96 GB'], ['NVMe SSD', '1.5 TB'], ['VRAM', '32 GB A100']],
  },
  {
    name: 'A100 Shared — 40GB VRAM',
    vramLabel: '40GB VRAM',
    price: '₹19,999/mo',
    note: '+ GST • Shared GPU',
    specs: [['vCPU', '42 Cores'], ['RAM', '128 GB'], ['NVMe SSD', '2 TB'], ['VRAM', '40 GB A100']],
  },
];

const dedicatedPlans = [
  {
    name: '1× A100 Dedicated',
    vramLabel: '1× 40GB VRAM',
    price: '₹29,999/mo',
    note: '+ GST • Dedicated GPU',
    specs: [['vCPU', '32 Cores'], ['RAM', '128 GB'], ['NVMe SSD', '2 TB'], ['GPU', '1× A100 40GB']],
  },
  {
    name: '2× A100 Dedicated',
    vramLabel: '2× 80GB VRAM',
    price: '₹59,999/mo',
    note: '+ GST • Dedicated GPU',
    specs: [['vCPU', '64 Cores'], ['RAM', '256 GB'], ['NVMe SSD', '4 TB'], ['GPU', '2× A100 80GB']],
  },
  {
    name: '4× A100 Dedicated',
    vramLabel: '4× 160GB VRAM',
    price: '₹1,19,999/mo',
    note: '+ GST • Dedicated GPU',
    specs: [['vCPU', '128 Cores'], ['RAM', '512 GB'], ['NVMe SSD', '8 TB'], ['GPU', '4× A100 160GB']],
  },
  {
    name: '8× A100 Dedicated',
    vramLabel: '8× 320GB VRAM',
    price: '₹2,39,999/mo',
    note: '+ GST • Dedicated GPU',
    specs: [['vCPU', '256 Cores'], ['RAM', '1 TB'], ['NVMe SSD', '16 TB'], ['GPU', '8× A100 320GB']],
  },
];

const gpuSpecs = [
  { label: 'GPU Model', value: 'A100 SXM4' },
  { label: 'Memory', value: '40GB HBM2e' },
  { label: 'CUDA Cores', value: '6,912' },
  { label: 'Tensor Cores', value: '432' },
  { label: 'Memory BW', value: '1,555 GB/s' },
  { label: 'FP32', value: '19.5 TFLOPS' },
  { label: 'TF32', value: '156 TFLOPS' },
  { label: 'NVLink', value: 'Yes' },
];

export default function NvidiaA100() {
  return (
    <div style={styles.page}>
      {/* Hero */}
      <div style={styles.hero}>
        <div style={{ ...styles.glowOrb, width: 450, height: 450, background: CYAN, top: -150, left: '5%' }} />
        <div style={{ ...styles.glowOrb, width: 400, height: 400, background: PURPLE, top: -120, right: '8%' }} />
        <div style={styles.heroBadge}>NVIDIA AMPERE A100</div>
        <h1 style={styles.heroTitle}>NVIDIA A100<br />GPU Cloud India</h1>
        <p style={styles.heroSub}>
          Ampere architecture with HBM2e memory and third-generation NVLink for breakthrough AI and HPC workloads.
        </p>
        <div style={styles.priceBadge}>Starting at ₹3,999/mo + GST</div>
      </div>

      {/* Section A: Shared */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>A100 GPU Cloud (Shared)</h2>
          <span style={styles.sectionTag}>Shared GPU</span>
        </div>
        <div style={styles.cardsGrid}>
          {sharedPlans.map((p) => <PlanCard key={p.name} plan={p} />)}
        </div>
      </div>

      {/* Section B: Dedicated */}
      <div style={{ ...styles.section, paddingTop: 0 }}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>A100 Dedicated GPU</h2>
          <span style={{ ...styles.sectionTag, borderColor: `${PURPLE}55`, color: PURPLE }}>Dedicated</span>
        </div>
        <div style={styles.cardsGrid}>
          {dedicatedPlans.map((p) => <PlanCard key={p.name} plan={p} />)}
        </div>
      </div>

      {/* GPU Spec */}
      <div style={{ padding: '0 20px' }}>
        <div style={styles.gpuSpecBox}>
          <h2 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff', margin: '0 0 4px' }}>GPU Specifications</h2>
          <div style={{ color: '#555', fontSize: 12, marginBottom: 4 }}>NVIDIA A100 SXM4 — Ampere Architecture</div>
          <div style={styles.gpuSpecGrid}>
            {gpuSpecs.map((s) => (
              <div key={s.label} style={styles.gpuSpecItem}>
                <div style={styles.gpuSpecLabel}>{s.label}</div>
                <div style={styles.gpuSpecValue}>{s.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
